if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        /*
         * =========================================================
         * THUMBNAIL CLICK
         * =========================================================
         * 1. Show clicked image
         * 2. Read image ALT
         * 3. Find matching Shopify Color option
         * 4. Select that real Shopify variant option
         */
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');

            if (!button) return;

            button.addEventListener('click', () => {
              const mediaId = mediaToSwitch.dataset.target;

              // First show the exact image user clicked
              this.setActiveMedia(mediaId, false);

              // Then sync its mapped Color variant
              this.selectVariantFromThumbnail(mediaToSwitch, mediaId);
            });
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /*
       * =========================================================
       * NORMALIZE VALUE
       * =========================================================
       *
       * "Matte Gold Finish"
       * becomes
       * "matte-gold-finish"
       *
       * "Multicolor"
       * becomes
       * "multicolor"
       */
      normalizeVariantValue(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      /*
       * =========================================================
       * FIND VARIANT FROM THUMBNAIL ALT
       * =========================================================
       */
      selectVariantFromThumbnail(thumbnailItem, mediaId) {
        if (!thumbnailItem) return;

        /*
         * Your thumbnail already contains:
         *
         * <img alt="multicolor">
         *
         * or:
         *
         * <img alt="matte-gold-finish">
         */
        const image = thumbnailItem.querySelector('img');

        if (!image) return;

        const imageAlt = this.normalizeVariantValue(
          image.getAttribute('alt')
        );

        // No ALT mapping = do nothing
        if (!imageAlt) return;

        /*
         * Stay inside this particular product.
         * Important if there are multiple products/sections on a page.
         */
        const productInfo = this.closest('product-info');

        if (!productInfo) return;

        const variantSelects =
          productInfo.querySelector('variant-selects');

        if (!variantSelects) return;

        /*
         * =========================================================
         * RADIO / SWATCH / BUTTON PICKER
         * =========================================================
         */
        const colorInputs = Array.from(
          variantSelects.querySelectorAll(
            'input[data-option-name]'
          )
        ).filter((input) => {
          const optionName = this.normalizeVariantValue(
            input.dataset.optionName
          );

          return (
            optionName === 'color' ||
            optionName === 'colour'
          );
        });

        const matchingInput = colorInputs.find((input) => {
          return (
            this.normalizeVariantValue(input.value) === imageAlt
          );
        });

        if (matchingInput) {
          // Already selected — nothing else required
          if (matchingInput.checked) {
            return;
          }

          /*
           * Shopify normally changes the gallery to the variant's
           * featured image after changing variant.
           *
           * But the user clicked a SPECIFIC thumbnail, so after
           * Shopify finishes changing the variant we restore the
           * exact image they clicked.
           */
          this.restoreClickedMediaAfterVariantChange(mediaId);

          /*
           * IMPORTANT:
           * Use click(), don't manually add active classes.
           *
           * This allows Shopify to update:
           * - selected variant
           * - price
           * - inventory
           * - Add to Cart variant ID
           * - URL
           * - availability
           * - swatch UI
           */
          matchingInput.click();

          return;
        }

        /*
         * =========================================================
         * DROPDOWN SUPPORT
         * =========================================================
         * Included in case you later change Color picker from
         * swatches/buttons to dropdown.
         */
        const colorOptions = Array.from(
          variantSelects.querySelectorAll(
            'option[data-option-name]'
          )
        ).filter((option) => {
          const optionName = this.normalizeVariantValue(
            option.dataset.optionName
          );

          return (
            optionName === 'color' ||
            optionName === 'colour'
          );
        });

        const matchingOption = colorOptions.find((option) => {
          return (
            this.normalizeVariantValue(option.value) === imageAlt
          );
        });

        if (!matchingOption) {
          return;
        }

        const select = matchingOption.closest('select');

        if (!select) return;

        if (select.value === matchingOption.value) {
          return;
        }

        this.restoreClickedMediaAfterVariantChange(mediaId);

        select.value = matchingOption.value;

        select.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );
      }

      /*
       * =========================================================
       * KEEP CLICKED IMAGE ACTIVE
       * =========================================================
       *
       * Example:
       *
       * Matte Gold has:
       * image 1 = featured image
       * image 2
       * image 3
       *
       * If user clicks image 3:
       *
       * 1. Matte Gold variant gets selected
       * 2. Shopify may temporarily activate image 1
       * 3. We put image 3 back as active
       */
      restoreClickedMediaAfterVariantChange(mediaId) {
        if (
          typeof subscribe !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined' ||
          !PUB_SUB_EVENTS.variantChange
        ) {
          /*
           * Fallback for themes where pub/sub isn't available.
           */
          window.setTimeout(() => {
            this.setActiveMedia(mediaId, false);
          }, 600);

          return;
        }

        const currentSectionId = this.id.replace(
          'MediaGallery-',
          ''
        );

        let timeoutId;

        const unsubscribe = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const sectionId = event?.data?.sectionId;

            /*
             * Ignore variant events belonging to another
             * product section.
             */
            if (
              sectionId &&
              String(sectionId) !== String(currentSectionId)
            ) {
              return;
            }

            unsubscribe();

            if (timeoutId) {
              clearTimeout(timeoutId);
            }

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.setActiveMedia(mediaId, false);
              });
            });
          }
        );

        /*
         * Safety cleanup in case Shopify doesn't publish
         * the event for some reason.
         */
        timeoutId = window.setTimeout(() => {
          unsubscribe();

          this.setActiveMedia(mediaId, false);
        }, 3000);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) {
          return;
        }

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia?.classList?.add('is-active');

        if (prepend) {
          if (
            activeMedia.parentElement.firstChild !== activeMedia
          ) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !==
                activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute('aria-current')
          );

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute('aria-current', true);
        }

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}